import { Button } from "@dashboard/components/Button";
import { Combobox } from "@dashboard/components/Combobox";
import { DashboardModal } from "@dashboard/components/Modal";
import PageHeader from "@dashboard/components/PageHeader";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@dashboard/config";
import { SearchProductFragment } from "@dashboard/graphql";
import { CommonSearchOpts } from "@dashboard/hooks/makeTopLevelSearch/types";
import { getSearchFetchMoreProps } from "@dashboard/hooks/makeTopLevelSearch/utils";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import useProductSearch from "@dashboard/searches/useProductSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MenuItem, TextField } from "@material-ui/core";
import { Box, Text } from "@saleor/macaw-ui-next";
import { Editor as TinyMceEditor } from "@tinymce/tinymce-react";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsRight,
  GripVertical,
  Plus,
} from "lucide-react";
import React from "react";

import {
  checkSaleorSlugCollision,
  createCmsPage,
  getCmsPage,
  updateCmsPage,
  uploadCmsMedia,
} from "../api";
import {
  CmsBlock,
  CmsBlockType,
  CmsMedia,
  CmsPageInput,
  CmsPageStatus,
  CmsProductReference,
} from "../types";
import { cmsPagesListUrl, cmsPageUrl } from "../urls";

interface CmsPageDetailsProps {
  id?: string;
}

const blockTypes: Array<{
  value: CmsBlockType;
  label: string;
  description: string;
  preview: "text" | "hero" | "split" | "faq" | "cta" | "gallery" | "products";
}> = [
  {
    value: "hero",
    label: "Hero",
    description: "Large page opener with headline, image, and primary action.",
    preview: "hero",
  },
  {
    value: "richText",
    label: "Rich text",
    description: "Formatted editorial content, tables, links, and inline images.",
    preview: "text",
  },
  {
    value: "imageText",
    label: "Image and text",
    description: "Two-column section for product, service, or brand storytelling.",
    preview: "split",
  },
  {
    value: "faq",
    label: "FAQ",
    description: "Question and answer rows for customer-service content.",
    preview: "faq",
  },
  {
    value: "cta",
    label: "CTA",
    description: "Focused conversion section with copy and a single link.",
    preview: "cta",
  },
  {
    value: "gallery",
    label: "Gallery",
    description: "Image grid with optional captions.",
    preview: "gallery",
  },
  {
    value: "productListing",
    label: "Product listing",
    description: "Curated storefront product cards populated from Saleor product IDs.",
    preview: "products",
  },
];

const pageTypes = [
  { value: "standard", label: "Standard" },
  { value: "landing", label: "Landing page" },
  { value: "legal", label: "Legal" },
  { value: "customer-service", label: "Customer service" },
  { value: "company", label: "Company" },
];

const TINYMCE_PLUGINS = ["autolink", "code", "image", "link", "lists", "table"];
const TINYMCE_TOOLBAR =
  "undo redo | blocks | bold italic strikethrough | link image blockquote code | bullist numlist | table tabledelete tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow tableinsertcolbefore tableinsertcolafter tabledeletecol | removeformat";
const RESERVED_CMS_ROOT_SLUGS = new Set([
  "account",
  "api",
  "blog",
  "brand",
  "cart",
  "category",
  "checkout",
  "content",
  "order-confirmation",
  "product",
  "products",
  "robots.txt",
  "search",
  "sitemap-index.xml",
  "sitemap.xml",
]);

const emptyForm: CmsPageInput = {
  title: "",
  slug: "",
  pageType: "standard",
  status: "draft",
  seoTitle: "",
  seoDescription: "",
  featuredImage: null,
  blocks: [],
};

const createBlock = (blockType: CmsBlockType): CmsBlock => {
  const base = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, blockType };

  if (blockType === "faq") {
    return { ...base, heading: "", items: [{ question: "", answer: "" }] };
  }

  if (blockType === "gallery") {
    return { ...base, heading: "", images: [] };
  }

  if (blockType === "imageText") {
    return { ...base, heading: "", body: "", image: null, imagePosition: "left" };
  }

  if (blockType === "productListing") {
    return { ...base, heading: "", body: "", productIds: [], products: [] };
  }

  return base;
};

const ensureBlockIds = (blocks: CmsBlock[]) =>
  blocks.map(block => ({
    ...block,
    id: block.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  }));

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s.-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const relationId = (value: string | CmsMedia | null | undefined) =>
  typeof value === "string" ? value : value?.id || null;

const productToReference = (product: SearchProductFragment): CmsProductReference => ({
  id: product.id,
  name: product.name,
  productTypeName: product.productType?.name,
  thumbnailUrl: product.thumbnail?.url,
});

const prepareBlocks = (blocks: CmsBlock[]) =>
  blocks.map(block => ({
    ...block,
    image: relationId(block.image),
    images: block.images?.map(item => ({
      ...item,
      image: relationId(item.image) || "",
    })),
  }));

const cmsPageToForm = (page: Awaited<ReturnType<typeof getCmsPage>>): CmsPageInput => ({
  title: page.title,
  slug: page.slug,
  pageType: page.pageType,
  status: page.status,
  seoTitle: page.seo?.title || "",
  seoDescription: page.seo?.description || "",
  featuredImage: page.featuredImage || null,
  blocks: ensureBlockIds(page.blocks || []),
});

const CmsPageDetails = ({ id }: CmsPageDetailsProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const [form, setForm] = React.useState<CmsPageInput>(emptyForm);
  const [loading, setLoading] = React.useState(Boolean(id));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isBlockPickerOpen, setBlockPickerOpen] = React.useState(false);
  const [collapsedBlockIds, setCollapsedBlockIds] = React.useState<Set<string>>(new Set());
  const [slugEdited, setSlugEdited] = React.useState(Boolean(id));
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const titleMissing = !form.title.trim();
  const slugMissing = !form.slug.trim();
  const canSave = !titleMissing && !slugMissing;
  const statusLabel = form.status === "published" ? "Published" : "Draft";
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  React.useEffect(() => {
    if (!id) {
      setForm(emptyForm);
      setSlugEdited(false);
      setLastSavedAt(null);

      return;
    }

    const loadPage = async () => {
      setLoading(true);
      setError(null);

      try {
        const page = await getCmsPage(id);

        setForm(cmsPageToForm(page));
        setSlugEdited(true);
        setLastSavedAt(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load CMS page");
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [id]);

  const handleTitleChange = (title: string) => {
    setForm(current => {
      const shouldSyncSlug =
        !slugEdited && (!current.slug || current.slug === slugify(current.title));

      return {
        ...current,
        title,
        slug: shouldSyncSlug ? slugify(title) : current.slug,
      };
    });
  };

  const handleSlugChange = (slug: string) => {
    setSlugEdited(true);
    changeField("slug", slugify(slug));
  };

  const changeField = (field: keyof CmsPageInput, value: CmsPageInput[keyof CmsPageInput]) => {
    setForm(current => ({
      ...current,
      [field]: value,
    }));
  };

  const changeBlock = (index: number, patch: Partial<CmsBlock>) => {
    setForm(current => ({
      ...current,
      blocks: current.blocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block,
      ),
    }));
  };

  const uploadImageFor = async (
    file: File,
    alt: string,
    onUploaded?: (media: CmsMedia) => void,
  ) => {
    try {
      const media = await uploadCmsMedia(file, alt);

      onUploaded?.(media);
      notify({ status: "success", text: "Image uploaded" });

      return media;
    } catch (err) {
      notify({
        status: "error",
        text: err instanceof Error ? err.message : "Unable to upload image",
      });

      return null;
    }
  };

  const save = async (status: CmsPageStatus, draft: boolean) => {
    if (!canSave) {
      setError("Title and slug are required.");

      return;
    }

    if (RESERVED_CMS_ROOT_SLUGS.has(form.slug)) {
      setError(
        `"${form.slug}" is reserved by the storefront and cannot be used as a CMS page slug.`,
      );

      return;
    }

    setSaving(true);
    setError(null);

    const input = {
      ...form,
      status,
      blocks: prepareBlocks(form.blocks),
      featuredImage: relationId(form.featuredImage),
    };

    try {
      if (status === "published") {
        const hasSaleorCollision = await checkSaleorSlugCollision(form.slug).catch(() => false);

        if (hasSaleorCollision) {
          const confirmed = window.confirm(
            `A Saleor page or legacy widget already uses "/${form.slug}". ` +
              "Publishing this Payload page will make the storefront render Payload content for that URL. Continue?",
          );

          if (!confirmed) {
            return;
          }
        }
      }

      let savedPage;

      if (id) {
        savedPage = await updateCmsPage(id, input, draft);
      } else {
        savedPage = await createCmsPage(input, draft);
      }

      setForm(cmsPageToForm(savedPage));
      setSlugEdited(true);
      setLastSavedAt(new Date());
      notify({
        status: "success",
        text: status === "published" ? "CMS page published" : "CMS draft saved",
      });

      if (!id) {
        navigate(cmsPageUrl(savedPage.id), { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save CMS page");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (blockType: CmsBlockType) => {
    setForm(current => ({
      ...current,
      blocks: [...current.blocks, createBlock(blockType)],
    }));
    setBlockPickerOpen(false);
  };

  const removeBlock = (index: number) => {
    const blockId = form.blocks[index]?.id;

    setForm(current => ({
      ...current,
      blocks: current.blocks.filter((_, blockIndex) => blockIndex !== index),
    }));

    if (blockId) {
      setCollapsedBlockIds(current => {
        const next = new Set(current);

        next.delete(blockId);

        return next;
      });
    }
  };

  const toggleBlockCollapsed = (blockId: string) => {
    setCollapsedBlockIds(current => {
      const next = new Set(current);

      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }

      return next;
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    setForm(current => {
      const oldIndex = current.blocks.findIndex(block => block.id === active.id);
      const newIndex = current.blocks.findIndex(block => block.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return current;
      }

      return {
        ...current,
        blocks: arrayMove(current.blocks, oldIndex, newIndex),
      };
    });
  };

  const expandAllBlocks = () => {
    setCollapsedBlockIds(new Set());
  };

  const collapseAllBlocks = () => {
    setCollapsedBlockIds(new Set(form.blocks.map(block => block.id).filter(Boolean) as string[]));
  };

  return (
    <>
      <Box paddingX={6} paddingTop={6}>
        <PageHeader title={id ? "Edit CMS Page" : "Create CMS Page"} />
      </Box>
      <Box paddingX={6} paddingBottom={20}>
        {loading ? (
          <Box padding={8} textAlign="center">
            <Text>Loading CMS page...</Text>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {error && <Text color="critical1">{error}</Text>}
            <Box display="grid" gap={3} style={{ gridTemplateColumns: "1fr 1fr" }}>
              <TextField
                label="Title"
                value={form.title}
                onChange={event => handleTitleChange(event.target.value)}
                variant="outlined"
                error={titleMissing}
                helperText={titleMissing ? "Title is required" : "Used in the editor and page SEO"}
                fullWidth
              />
              <TextField
                label="Slug"
                value={form.slug}
                onChange={event => handleSlugChange(event.target.value)}
                variant="outlined"
                error={slugMissing}
                helperText={slugMissing ? "Slug is required" : `Storefront path: /${form.slug}`}
                fullWidth
              />
              <TextField
                select
                label="Page type"
                value={form.pageType}
                onChange={event => changeField("pageType", event.target.value)}
                variant="outlined"
                fullWidth
              >
                {pageTypes.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="SEO title"
                value={form.seoTitle}
                onChange={event => changeField("seoTitle", event.target.value)}
                variant="outlined"
                fullWidth
              />
            </Box>
            <TextField
              label="SEO description"
              value={form.seoDescription}
              onChange={event => changeField("seoDescription", event.target.value)}
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
            />
            <Box display="flex" flexDirection="column" gap={2}>
              <Text size={3} fontWeight="bold">
                Featured image
              </Text>
              <input
                type="file"
                accept="image/*"
                onChange={event => {
                  const file = event.target.files?.[0];

                  if (file) {
                    uploadImageFor(file, form.title || file.name, media =>
                      changeField("featuredImage", media),
                    );
                  }
                }}
              />
              {relationId(form.featuredImage) && (
                <Text size={2} color="default2">
                  Selected media ID: {relationId(form.featuredImage)}
                </Text>
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Text size={5} fontWeight="bold">
                  Page blocks
                </Text>
                <Box display="flex" gap={2}>
                  <Button
                    variant="secondary"
                    disabled={form.blocks.length === 0}
                    onClick={expandAllBlocks}
                  >
                    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                      <ChevronsDown size={16} />
                      Expand all
                    </Box>
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={form.blocks.length === 0}
                    onClick={collapseAllBlocks}
                  >
                    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                      <ChevronsRight size={16} />
                      Collapse all
                    </Box>
                  </Button>
                  <Button variant="secondary" onClick={() => setBlockPickerOpen(true)}>
                    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                      <Plus size={16} />
                      Add block
                    </Box>
                  </Button>
                </Box>
              </Box>
              {form.blocks.length === 0 ? (
                <Box
                  alignItems="center"
                  backgroundColor="default1"
                  borderColor="default1"
                  borderRadius={3}
                  borderStyle="solid"
                  borderWidth={1}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  justifyContent="center"
                  padding={8}
                  textAlign="center"
                >
                  <Text size={4} fontWeight="bold">
                    Start with a content block
                  </Text>
                  <Text size={2} color="default2">
                    Add a hero, rich text, FAQ, gallery, CTA, or product listing section.
                  </Text>
                  <Button variant="secondary" onClick={() => setBlockPickerOpen(true)}>
                    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                      <Plus size={16} />
                      Add block
                    </Box>
                  </Button>
                </Box>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={form.blocks.map(block => block.id || "")}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box display="flex" flexDirection="column" gap={3}>
                      {form.blocks.map((block, index) => (
                        <SortableCmsBlock
                          key={block.id}
                          block={block}
                          collapsed={Boolean(block.id && collapsedBlockIds.has(block.id))}
                          index={index}
                          totalBlocks={form.blocks.length}
                          onChange={patch => changeBlock(index, patch)}
                          onRemove={() => removeBlock(index)}
                          onToggleCollapsed={() => block.id && toggleBlockCollapsed(block.id)}
                          onUpload={uploadImageFor}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              )}
            </Box>
            <Box
              backgroundColor="default1"
              borderColor="default1"
              borderStyle="solid"
              borderWidth={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={4}
              paddingX={6}
              paddingY={4}
              style={{
                bottom: 0,
                height: "65px",
                flexWrap: "wrap",
                left: 260,
                position: "fixed",
                right: -20,
                zIndex: 1200,
              }}
            >
              <Box display="flex" flexDirection="column" gap={1} style={{ minWidth: 0 }}>
                <Text size={2} fontWeight="bold">
                  {statusLabel} · /{form.slug || "new-page"} · {form.blocks.length}{" "}
                  {form.blocks.length === 1 ? "block" : "blocks"}
                </Text>
                <Text size={2} color="default2">
                  {lastSavedAt
                    ? `Saved at ${lastSavedAt.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                      })}`
                    : "Changes stay on this page after saving."}
                </Text>
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2} style={{ flexWrap: "wrap" }}>
                <Button
                  variant="secondary"
                  disabled={saving}
                  onClick={() => navigate(cmsPagesListUrl())}
                >
                  Back
                </Button>
                <Button
                  variant="secondary"
                  disabled={saving || !canSave}
                  onClick={() => save("draft", true)}
                >
                  Save Draft
                </Button>
                {id && (
                  <Button
                    variant="secondary"
                    disabled={saving || !canSave}
                    onClick={() => save("draft", false)}
                  >
                    Unpublish
                  </Button>
                )}
                <Button
                  variant="primary"
                  disabled={saving || !canSave}
                  onClick={() => save("published", false)}
                >
                  Publish
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <BlockPickerModal
        open={isBlockPickerOpen}
        onClose={() => setBlockPickerOpen(false)}
        onSelect={addBlock}
      />
    </>
  );
};

interface BlockFieldsProps {
  block: CmsBlock;
  onChange: (patch: Partial<CmsBlock>) => void;
  onUpload: (
    file: File,
    alt: string,
    callback?: (media: CmsMedia) => void,
  ) => Promise<CmsMedia | null>;
}

interface SortableCmsBlockProps extends BlockFieldsProps {
  collapsed: boolean;
  index: number;
  totalBlocks: number;
  onRemove: () => void;
  onToggleCollapsed: () => void;
}

const SortableCmsBlock = ({
  block,
  collapsed,
  index,
  totalBlocks,
  onChange,
  onRemove,
  onToggleCollapsed,
  onUpload,
}: SortableCmsBlockProps) => {
  const sortableId = block.id || `${block.blockType}-${index}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });
  const blockLabel =
    blockTypes.find(type => type.value === block.blockType)?.label || block.blockType;

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
      }}
      backgroundColor="default1"
      borderColor="default1"
      borderRadius={3}
      borderStyle="solid"
      borderWidth={1}
      display="flex"
      flexDirection="column"
      gap={collapsed ? 0 : 3}
      padding={4}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
        <Box display="flex" alignItems="center" gap={2} style={{ minWidth: 0 }}>
          <button
            type="button"
            aria-label={`Drag ${blockLabel} block`}
            {...attributes}
            {...listeners}
            style={{
              alignItems: "center",
              background: "transparent",
              border: 0,
              cursor: "grab",
              display: "inline-flex",
              padding: 0,
              touchAction: "none",
            }}
          >
            <GripVertical size={18} />
          </button>
          <button
            type="button"
            aria-label={`${collapsed ? "Expand" : "Collapse"} ${blockLabel} block`}
            onClick={onToggleCollapsed}
            style={{
              alignItems: "center",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              display: "inline-flex",
              padding: 0,
            }}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          </button>
          <Box display="flex" flexDirection="column" gap={1} style={{ minWidth: 0 }}>
            <Text fontWeight="bold">{blockLabel}</Text>
            <Text size={2} color="default2">
              Block {index + 1} of {totalBlocks}
            </Text>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="secondary" onClick={onRemove}>
            Remove
          </Button>
        </Box>
      </Box>
      {!collapsed && <BlockFields block={block} onChange={onChange} onUpload={onUpload} />}
    </Box>
  );
};

interface BlockPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (blockType: CmsBlockType) => void;
}

const BlockPickerModal = ({ open, onClose, onSelect }: BlockPickerModalProps) => (
  <DashboardModal onChange={isOpen => !isOpen && onClose()} open={open}>
    <DashboardModal.Content size="md">
      <DashboardModal.Header>Add CMS block</DashboardModal.Header>
      <Box display="grid" gap={4} __gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))">
        {blockTypes.map(blockType => (
          <button
            key={blockType.value}
            type="button"
            onClick={() => onSelect(blockType.value)}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
          >
            <Box
              borderColor="default1"
              borderRadius={3}
              borderStyle="solid"
              borderWidth={1}
              display="flex"
              flexDirection="column"
              gap={3}
              padding={4}
              __height="100%"
              __transition="border-color 120ms ease, box-shadow 120ms ease"
            >
              <BlockPreview variant={blockType.preview} />
              <Box display="flex" flexDirection="column" gap={1}>
                <Text fontWeight="bold">{blockType.label}</Text>
                <Text size={2} color="default2">
                  {blockType.description}
                </Text>
              </Box>
            </Box>
          </button>
        ))}
      </Box>
      <DashboardModal.Actions>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </DashboardModal.Actions>
    </DashboardModal.Content>
  </DashboardModal>
);

const BlockPreview = ({ variant }: { variant: (typeof blockTypes)[number]["preview"] }) => (
  <Box
    backgroundColor="default2"
    borderColor="default1"
    borderRadius={2}
    borderStyle="solid"
    borderWidth={1}
    padding={3}
    __height={140}
  >
    {variant === "hero" && (
      <Box display="grid" gap={2} __gridTemplateColumns="1.15fr 0.85fr" __height="100%">
        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
          <PreviewLine width="32%" />
          <PreviewLine height={18} width="86%" />
          <PreviewLine width="70%" />
          <PreviewPill width={84} />
        </Box>
        <PreviewImage />
      </Box>
    )}
    {variant === "text" && (
      <Box display="flex" flexDirection="column" gap={2}>
        <PreviewLine height={16} width="58%" />
        <PreviewLine />
        <PreviewLine width="94%" />
        <PreviewLine width="88%" />
        <PreviewLine width="72%" />
        <Box display="flex" gap={2} paddingTop={2}>
          <PreviewPill width={64} />
          <PreviewPill width={46} />
        </Box>
      </Box>
    )}
    {variant === "split" && (
      <Box display="grid" gap={3} __gridTemplateColumns="1fr 1fr" __height="100%">
        <PreviewImage />
        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
          <PreviewLine height={16} width="72%" />
          <PreviewLine />
          <PreviewLine width="84%" />
          <PreviewLine width="62%" />
        </Box>
      </Box>
    )}
    {variant === "faq" && (
      <Box display="flex" flexDirection="column" gap={2}>
        <PreviewLine height={16} width="48%" />
        {[0, 1, 2].map(item => (
          <Box
            key={item}
            backgroundColor="default1"
            borderColor="default1"
            borderRadius={2}
            borderStyle="solid"
            borderWidth={1}
            padding={2}
          >
            <PreviewLine width={item === 2 ? "68%" : "82%"} />
          </Box>
        ))}
      </Box>
    )}
    {variant === "cta" && (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap={2}
        justifyContent="center"
        __height="100%"
      >
        <PreviewLine height={18} width="68%" />
        <PreviewLine width="78%" />
        <PreviewPill width={96} />
      </Box>
    )}
    {variant === "gallery" && (
      <Box display="grid" gap={2} __gridTemplateColumns="repeat(3, 1fr)" __height="100%">
        {[0, 1, 2, 3, 4, 5].map(item => (
          <PreviewImage key={item} />
        ))}
      </Box>
    )}
    {variant === "products" && (
      <Box display="flex" flexDirection="column" gap={3} __height="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <PreviewLine height={16} width="44%" />
          <PreviewPill width={72} />
        </Box>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(3, 1fr)" __height="100%">
          {[0, 1, 2].map(item => (
            <Box
              key={item}
              backgroundColor="default1"
              borderColor="default1"
              borderRadius={2}
              borderStyle="solid"
              borderWidth={1}
              display="flex"
              flexDirection="column"
              gap={1}
              padding={2}
            >
              <Box backgroundColor="default2" borderRadius={1} __height={42} />
              <PreviewLine width="86%" />
              <PreviewLine width="58%" />
            </Box>
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

const PreviewLine = ({ height = 10, width = "100%" }: { height?: number; width?: string }) => (
  <Box backgroundColor="default1" borderRadius={1} __height={height} __width={width} />
);

const PreviewPill = ({ width }: { width: number }) => (
  <Box backgroundColor="default1" borderRadius={3} __height={24} __width={width} />
);

const PreviewImage = () => (
  <Box
    backgroundColor="default1"
    borderColor="default1"
    borderRadius={2}
    borderStyle="solid"
    borderWidth={1}
    __height="100%"
    __minHeight={36}
  />
);

const BlockFields = ({ block, onChange, onUpload }: BlockFieldsProps) => {
  const imageInput = (
    <>
      <input
        type="file"
        accept="image/*"
        onChange={event => {
          const file = event.target.files?.[0];

          if (file) {
            void onUpload(file, block.heading || file.name, media => onChange({ image: media }));
          }
        }}
      />
      {relationId(block.image) && (
        <Text size={2} color="default2">
          Selected media ID: {relationId(block.image)}
        </Text>
      )}
    </>
  );

  if (block.blockType === "richText") {
    return (
      <CmsRichTextEditor
        value={block.content || ""}
        onChange={content => onChange({ content })}
        onUpload={onUpload}
      />
    );
  }

  if (block.blockType === "hero") {
    return (
      <>
        <TextField
          label="Eyebrow"
          value={block.eyebrow || ""}
          onChange={event => onChange({ eyebrow: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Subheading"
          value={block.subheading || ""}
          onChange={event => onChange({ subheading: event.target.value })}
          variant="outlined"
          multiline
          minRows={3}
          fullWidth
        />
        {imageInput}
        <TextField
          label="Primary button label"
          value={block.primaryLabel || ""}
          onChange={event => onChange({ primaryLabel: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Primary button href"
          value={block.primaryHref || ""}
          onChange={event => onChange({ primaryHref: event.target.value })}
          variant="outlined"
          fullWidth
        />
      </>
    );
  }

  if (block.blockType === "imageText") {
    return (
      <>
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Body"
          value={block.body || ""}
          onChange={event => onChange({ body: event.target.value })}
          variant="outlined"
          multiline
          minRows={4}
          fullWidth
        />
        {imageInput}
        <TextField
          select
          label="Image position"
          value={block.imagePosition || "left"}
          onChange={event => onChange({ imagePosition: event.target.value as "left" | "right" })}
          variant="outlined"
          fullWidth
        >
          <MenuItem value="left">Left</MenuItem>
          <MenuItem value="right">Right</MenuItem>
        </TextField>
      </>
    );
  }

  if (block.blockType === "faq") {
    const items = block.items || [];

    return (
      <>
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        {items.map((item, index) => (
          <Box key={index} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Question"
              value={item.question}
              onChange={event =>
                onChange({
                  items: items.map((current, itemIndex) =>
                    itemIndex === index ? { ...current, question: event.target.value } : current,
                  ),
                })
              }
              variant="outlined"
              fullWidth
            />
            <TextField
              label="Answer"
              value={item.answer}
              onChange={event =>
                onChange({
                  items: items.map((current, itemIndex) =>
                    itemIndex === index ? { ...current, answer: event.target.value } : current,
                  ),
                })
              }
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
            />
          </Box>
        ))}
        <Button
          variant="secondary"
          onClick={() => onChange({ items: [...items, { question: "", answer: "" }] })}
        >
          Add FAQ item
        </Button>
      </>
    );
  }

  if (block.blockType === "cta") {
    return (
      <>
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Body"
          value={block.body || ""}
          onChange={event => onChange({ body: event.target.value })}
          variant="outlined"
          multiline
          minRows={3}
          fullWidth
        />
        <TextField
          label="Button label"
          value={block.label || ""}
          onChange={event => onChange({ label: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Button href"
          value={block.href || ""}
          onChange={event => onChange({ href: event.target.value })}
          variant="outlined"
          fullWidth
        />
      </>
    );
  }

  if (block.blockType === "productListing") {
    return (
      <>
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        <TextField
          label="Body"
          value={block.body || ""}
          onChange={event => onChange({ body: event.target.value })}
          variant="outlined"
          multiline
          minRows={3}
          fullWidth
        />
        <ProductListingPicker
          productIds={block.productIds || []}
          products={block.products || []}
          onChange={(productIds, products) => onChange({ productIds, products })}
        />
      </>
    );
  }

  if (block.blockType === "gallery") {
    const images = block.images || [];

    return (
      <>
        <TextField
          label="Heading"
          value={block.heading || ""}
          onChange={event => onChange({ heading: event.target.value })}
          variant="outlined"
          fullWidth
        />
        {images.map((item, index) => (
          <Box key={index} display="flex" flexDirection="column" gap={2}>
            <Text size={2}>
              Image {index + 1}: {relationId(item.image)}
            </Text>
            <TextField
              label="Caption"
              value={item.caption || ""}
              onChange={event =>
                onChange({
                  images: images.map((current, itemIndex) =>
                    itemIndex === index ? { ...current, caption: event.target.value } : current,
                  ),
                })
              }
              variant="outlined"
              fullWidth
            />
          </Box>
        ))}
        <input
          type="file"
          accept="image/*"
          onChange={event => {
            const file = event.target.files?.[0];

            if (file) {
              void onUpload(file, block.heading || file.name, media =>
                onChange({ images: [...images, { image: media, caption: "" }] }),
              );
            }
          }}
        />
      </>
    );
  }

  return null;
};

interface ProductListingPickerProps {
  productIds: string[];
  products: CmsProductReference[];
  onChange: (productIds: string[], products: CmsProductReference[]) => void;
}

const ProductListingPicker = ({ productIds, products, onChange }: ProductListingPickerProps) => {
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts,
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });
  const searchResults = React.useMemo(
    () => mapEdgesToItems(searchProductsOpts.data?.search) || [],
    [searchProductsOpts.data?.search],
  );
  const fetchMoreProducts = getSearchFetchMoreProps(
    searchProductsOpts as CommonSearchOpts,
    loadMoreProducts,
  );
  const productOptions = searchResults
    .filter(product => !productIds.includes(product.id))
    .map(product => ({
      label: product.name,
      value: product.id,
    }));
  const productSnapshots = React.useMemo(() => {
    const searchProductsMap = new Map(
      searchResults.map(product => [product.id, productToReference(product)]),
    );
    const savedProductsMap = new Map(products.map(product => [product.id, product]));

    return productIds.map(
      id => searchProductsMap.get(id) || savedProductsMap.get(id) || { id, name: id },
    );
  }, [productIds, products, searchResults]);

  const addProduct = (productId: string | null) => {
    if (!productId || productIds.includes(productId)) {
      return;
    }

    const product = searchResults.find(item => item.id === productId);
    const nextProduct = product ? productToReference(product) : { id: productId, name: productId };

    onChange([...productIds, productId], [...productSnapshots, nextProduct]);
  };

  const removeProduct = (productId: string) => {
    onChange(
      productIds.filter(id => id !== productId),
      productSnapshots.filter(product => product.id !== productId),
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Combobox
        label="Add Saleor product"
        name="productListingPicker"
        value={null}
        options={productOptions}
        fetchOptions={searchProducts}
        fetchMore={fetchMoreProducts}
        loading={searchProductsOpts.loading}
        alwaysFetchOnFocus
        onChange={event => addProduct(event.target.value as string | null)}
      />
      <Text size={2} color="default2">
        {productIds.length} product ID{productIds.length === 1 ? "" : "s"} selected
      </Text>
      <Box
        borderColor="default1"
        borderRadius={2}
        borderStyle="solid"
        borderWidth={1}
        display="flex"
        gap={2}
        padding={3}
        style={{ overflowX: "auto" }}
        __minHeight={154}
      >
        {productSnapshots.length === 0 ? (
          <Box alignItems="center" display="flex" __minWidth="100%">
            <Text size={2} color="default2">
              No products selected.
            </Text>
          </Box>
        ) : (
          productSnapshots.map(product => (
            <Box
              key={product.id}
              backgroundColor="default1"
              borderColor="default1"
              borderRadius={2}
              borderStyle="solid"
              borderWidth={1}
              display="flex"
              flexDirection="column"
              gap={2}
              padding={3}
              __minWidth={220}
              __width={220}
            >
              <Box
                alignItems="center"
                backgroundColor="default2"
                borderRadius={2}
                display="flex"
                justifyContent="center"
                style={{ overflow: "hidden" }}
                __height={88}
              >
                {product.thumbnailUrl ? (
                  <img
                    alt=""
                    src={product.thumbnailUrl}
                    style={{
                      height: "100%",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                ) : (
                  <Text size={2} color="default2">
                    No image
                  </Text>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap={1} style={{ minWidth: 0 }}>
                <Text fontWeight="bold" style={{ overflowWrap: "anywhere" }}>
                  {product.name}
                </Text>
                {product.productTypeName ? (
                  <Text size={2} color="default2">
                    {product.productTypeName}
                  </Text>
                ) : null}
                <Text size={1} color="default2" style={{ overflowWrap: "anywhere" }}>
                  {product.id}
                </Text>
              </Box>
              <Button variant="secondary" onClick={() => removeProduct(product.id)}>
                Remove
              </Button>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

interface CmsRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onUpload: BlockFieldsProps["onUpload"];
}

const CmsRichTextEditor = ({ value, onChange, onUpload }: CmsRichTextEditorProps) => {
  const pickImageFile = React.useCallback(
    (callback: (value: string, meta?: Record<string, unknown>) => void) => {
      const input = document.createElement("input");

      input.type = "file";
      input.accept = "image/*";
      input.addEventListener("change", async () => {
        const file = input.files?.[0];

        if (!file) {
          return;
        }

        const media = await onUpload(file, file.name);

        if (media?.url) {
          callback(media.url, { alt: media.alt || file.name, title: media.filename || file.name });
        }
      });
      input.click();
    },
    [onUpload],
  );

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Text size={2} color="default2">
        Content
      </Text>
      <TinyMceEditor
        value={value}
        licenseKey={(import.meta.env.VITE_TINYMCE_LICENSE_KEY as string | undefined) || "gpl"}
        tinymceScriptSrc={`${import.meta.env.BASE_URL}tinymce/tinymce.min.js`}
        onEditorChange={onChange}
        init={{
          automatic_uploads: true,
          block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3",
          branding: false,
          content_style: "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
          file_picker_callback: (callback, _value, meta) => {
            if (meta.filetype === "image") {
              pickImageFile(callback);
            }
          },
          file_picker_types: "image",
          height: 360,
          image_title: true,
          images_upload_handler: async blobInfo => {
            const blob = blobInfo.blob();
            const file =
              blob instanceof File
                ? blob
                : new File([blob], blobInfo.filename(), {
                    type: blob.type || "application/octet-stream",
                  });
            const media = await onUpload(file, file.name);

            if (!media?.url) {
              throw new Error("Unable to upload image");
            }

            return media.url;
          },
          menubar: false,
          paste_data_images: true,
          plugins: TINYMCE_PLUGINS,
          statusbar: false,
          toolbar: TINYMCE_TOOLBAR,
        }}
      />
    </Box>
  );
};

export default CmsPageDetails;
